import {useSidebar} from '@/contexts/SidebarContext/SidebarContext';
import {Box, Theme} from '@mui/material';
import {Suspense} from 'react';
import {Panel, PanelGroup, PanelResizeHandle} from 'react-resizable-panels';
import {Outlet} from 'react-router-dom';
import {
  PortalContainer,
  SplitLayoutContext,
  useSplitLayoutState,
} from './SplitLayoutContext';
import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
  SUBSIDEBAR_WIDTH,
} from './sidebarWidths';

const panelStyles = {
  borderRadius: 2,
  border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
  backgroundColor: (theme: Theme) => theme.palette.background.default,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
} as const;

const ResizeHandle = () => (
  <PanelResizeHandle
    style={{
      width: 12,
      background: 'transparent',
      cursor: 'col-resize',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Box
      sx={{
        width: 4,
        height: 40,
        borderRadius: 1,
        backgroundColor: 'divider',
      }}
    />
  </PanelResizeHandle>
);

export const SplitLayout = () => {
  const {
    selectedCategory,
    isSubSidebarCollapsed,
    isSidebarPinned,
    getItemsForCategory,
  } = useSidebar();
  const categoryItems = selectedCategory
    ? getItemsForCategory(selectedCategory)
    : [];
  const hasSubSidebar = selectedCategory !== null && categoryItems.length > 0;

  const splitLayoutState = useSplitLayoutState();

  const contentMarginLeft = () => {
    const sidebarWidth = isSidebarPinned
      ? SIDEBAR_EXPANDED_WIDTH
      : SIDEBAR_COLLAPSED_WIDTH;

    if (!hasSubSidebar) {
      return `${sidebarWidth + 8}px`;
    }
    if (isSubSidebarCollapsed) {
      return `${sidebarWidth + 8}px`;
    }
    return `${sidebarWidth + SUBSIDEBAR_WIDTH + 8}px`;
  };

  return (
    <SplitLayoutContext.Provider value={splitLayoutState}>
      <Box
        sx={{
          flex: 1,
          marginLeft: contentMarginLeft(),
          marginRight: 1,
          marginTop: 1,
          marginBottom: 1,
          height: 'calc(100vh - 16px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Box sx={panelStyles}>
          <PortalContainer
            id="split-header-container"
            onMount={splitLayoutState.setHeaderPortal}
          />
        </Box>
        <Box sx={{flex: 1, display: 'flex', minHeight: 0}}>
          <PanelGroup
            direction="horizontal"
            autoSaveId="conversations-panel-sizes"
          >
            <Panel
              defaultSize={splitLayoutState.leftPanelConfig.defaultSize}
              minSize={splitLayoutState.leftPanelConfig.minSize}
              maxSize={splitLayoutState.leftPanelConfig.maxSize}
            >
              <Box sx={{...panelStyles, height: '100%'}}>
                <PortalContainer
                  id="split-left-container"
                  onMount={splitLayoutState.setLeftPortal}
                />
              </Box>
            </Panel>
            <ResizeHandle />
            <Panel
              minSize={splitLayoutState.rightPanelConfig.minSize}
              maxSize={splitLayoutState.rightPanelConfig.maxSize}
            >
              <Box sx={{...panelStyles, height: '100%'}}>
                <PortalContainer
                  id="split-right-container"
                  onMount={splitLayoutState.setRightPortal}
                />
              </Box>
            </Panel>
          </PanelGroup>
        </Box>
      </Box>
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    </SplitLayoutContext.Provider>
  );
};
